//sapiadmin/src/pages/orders

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, orderBy, doc, updateDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import  {SHIPPING_CARRIERS } from '../utils/orderService';
import {
  RefreshCw, BarChart2, CheckSquare, Search, X, Eye,
  Check, XCircle, Package, Truck, ChevronDown, Copy,
  Filter, ShoppingBag,
  Clock,
  TrendingUp
} from 'react-feather';
import FormatPrice from '../components/FormatPrice';
import { BoxSelect, CheckCircle2 } from 'lucide-react';
import { sendWhatsAppMessage, sendWhatsAppTemplate } from '../services/whatsappService';
import { sendAndCleanTokens } from '../utils/notificationHelper';

// ─── Status / Priority tokens ─────────────────────────────────────────────
const STATUS_MAP = {
  Placed:    { label:'Placed',    bg:'rgba(245,158,11,.12)',  color:'#fbbf24', ring:'rgba(245,158,11,.3)'  },
  Approved:  { label:'Approved',  bg:'rgba(56,189,248,.12)',  color:'#38bdf8', ring:'rgba(56,189,248,.3)'  },
  Packed:    { label:'Packed',    bg:'rgba(99,102,241,.12)',  color:'#818cf8', ring:'rgba(99,102,241,.3)'  }, // ✅ DÉJÀ PRÉSENT
  Shipped:   { label:'Shipped',   bg:'rgba(139,92,246,.12)', color:'#a78bfa', ring:'rgba(139,92,246,.3)'  },
  Delivered: { label:'Delivered', bg:'rgba(16,185,129,.12)', color:'#34d399', ring:'rgba(16,185,129,.3)'  },
  Declined:  { label:'Declined',  bg:'rgba(239,68,68,.12)',  color:'#f87171', ring:'rgba(239,68,68,.3)'   },
  Cancelled: { label:'Cancelled', bg:'rgba(100,116,139,.12)',color:'#94a3b8', ring:'rgba(100,116,139,.3)' },
  Refunded:  { label:'Refunded',  bg:'rgba(100,116,139,.12)',color:'#94a3b8', ring:'rgba(100,116,139,.3)' },
};

const PRIORITY_MAP = {
  urgent: { label:'Urgent', color:'#f87171' },
  high:   { label:'High',   color:'#fb923c' },
  normal: { label:'Normal', color:'#94a3b8' },
  low:    { label:'Low',    color:'#38bdf8' },
};

// ─── Atoms ────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.Cancelled;
  return (
    <span style={{
      background:s.bg, color:s.color,
      boxShadow:`inset 0 0 0 1px ${s.ring}`,
      fontSize:10, fontWeight:700, letterSpacing:'.06em',
      textTransform:'uppercase', padding:'3px 8px', borderRadius:20,
      whiteSpace:'nowrap'
    }}>{s.label}</span>
  );
};

const PriorityDot = ({ priority }) => {
  const p = PRIORITY_MAP[priority] || PRIORITY_MAP.normal;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:10, color:p.color, fontWeight:600 }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:p.color, display:'inline-block' }} />
      {p.label}
    </span>
  );
};

const GlassPanel = ({ children, style={} }) => (
  <div style={{
    background:'rgba(17,24,39,.85)', border:'1px solid rgba(255,255,255,.06)',
    borderRadius:16, backdropFilter:'blur(16px)', ...style
  }}>{children}</div>
);

const Btn = ({ children, onClick, variant='ghost', size='sm', disabled=false, style={}, type='button' }) => {
  const V = {
    ghost:  { background:'rgba(255,255,255,.06)', color:'#cbd5e1', border:'1px solid rgba(255,255,255,.08)' },
    amber:  { background:'rgba(245,158,11,.15)',  color:'#f59e0b', border:'1px solid rgba(245,158,11,.3)'  },
    green:  { background:'rgba(16,185,129,.15)',  color:'#34d399', border:'1px solid rgba(16,185,129,.3)'  },
    red:    { background:'rgba(239,68,68,.15)',   color:'#f87171', border:'1px solid rgba(239,68,68,.3)'   },
    indigo: { background:'rgba(99,102,241,.15)',  color:'#818cf8', border:'1px solid rgba(99,102,241,.3)'  },
    violet: { background:'rgba(139,92,246,.15)',  color:'#a78bfa', border:'1px solid rgba(139,92,246,.3)'  },
  };
  const v = V[variant]||V.ghost;
  const pad = size==='xs' ? '3px 8px' : size==='sm' ? '5px 12px' : '8px 16px';
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      ...v, padding:pad, borderRadius:8, fontSize:11, fontWeight:600,
      cursor:disabled?'not-allowed':'pointer', opacity:disabled?.45:1,
      display:'inline-flex', alignItems:'center', gap:5,
      transition:'opacity .15s', whiteSpace:'nowrap', ...style
    }}>{children}</button>
  );
};

const DarkInput = ({ value, onChange, placeholder, type='text', style={} }) => (
  <input type={type} value={value} onChange={onChange} placeholder={placeholder}
    style={{ background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.1)', borderRadius:10, padding:'8px 12px', color:'#e2e8f0', fontSize:12, width:'100%', outline:'none', ...style }} />
);

const DarkSelect = ({ value, onChange, children, style={} }) => (
  <select value={value} onChange={onChange}
    style={{ background:'#111827', border:'1px solid rgba(255,255,255,.1)', borderRadius:10, padding:'8px 12px', color:'#e2e8f0', fontSize:12, width:'100%', outline:'none', appearance:'none', ...style }}>
    {children}
  </select>
);

const MiniStat = ({ label, value, accent="#14b8a6", icon:Icon, index=0 }) => (
  <motion.div initial={{ opacity:0, y:18 }} animate={{ opacity:1, y:0 }} transition={{ delay:index*.07, duration:.45, ease:[.22,1,.36,1] }} whileHover={{ y:-3, transition:{ duration:.15 } }}
    style={{ background:"rgba(17,24,39,.85)", border:`1px solid ${accent}33`, borderRadius:14, padding:"14px 16px", boxShadow:`0 0 24px ${accent}0d`, position:"relative", overflow:"hidden" }}>
    <div style={{ position:"absolute", top:-24, right:-24, width:72, height:72, borderRadius:"50%", background:`${accent}18`, filter:"blur(20px)", pointerEvents:"none" }} />
    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", position:"relative" }}>
      <div>
        <p style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".14em", color:"#475569", marginBottom:8 }}>{label}</p>
        <p style={{ fontSize:22, fontWeight:700, color:"#fff", lineHeight:1 }}>{value}</p>
      </div>
      {Icon && <div style={{ width:32, height:32, borderRadius:9, background:`${accent}18`, border:`1px solid ${accent}33`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Icon size={15} color={accent} strokeWidth={1.8} /></div>}
    </div>
    <motion.div initial={{ scaleX:0 }} animate={{ scaleX:1 }} transition={{ duration:.5, delay:.2+index*.07 }} style={{ position:"absolute", bottom:0, left:0, right:0, height:2, background:accent, opacity:.5, transformOrigin:"left" }} />
  </motion.div>
);

// ═══════════════════════════════════════════════════════════════════════════
function Orders() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    status:'all', priority:'all', searchTerm:'',
    dateRange:{ startDate:'', endDate:'' }, minAmount:'', carrier:'all'
  });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [processingAction, setProcessingAction] = useState(false);

  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);

  const [shippingInfo, setShippingInfo] = useState({ trackingNumber:'', carrier:'IndiaPost', service:'standard', weight:'', notes:'' });
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const getOrderTotal = useCallback((order) => {
    for (const f of [order.total,order.amount,order.grandTotal,order.finalAmount,order.orderTotal,order.financials?.total,order.payment?.amount]) {
      if (f!=null && f!==0 && !isNaN(f)) return Number(f);
    }
    return order.items ? order.items.reduce((s,i)=>s+(i.price||0)*(i.quantity||0),0) : 0;
  }, []);

  const formatDate   = (d) => { try { return new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}); } catch { return '—'; } };
  // const getDisplayId = (o) => { const id=o.orderId||o.id||''; return '#'+(id.length>8?id.substring(0,8):id).toUpperCase(); };
  const getDisplayId = (o) => { const id=o.orderId||o.id||''; return '#'+(id).toUpperCase(); };
  const getFullId    = (o) => o.orderId||o.id||'N/A';
  const copyToClipboard = (t) => { navigator.clipboard.writeText(t); toast.success('Copied!'); };

  // ─── ÉCOUTE EN TEMPS RÉEL AVEC onSnapshot ──────────────────────────────
useEffect(() => {
  setLoading(true);
  setError(null);

  // Essayer d'abord avec orderBy('orderDate', 'desc')
  let q;
  try {
    q = query(collection(db, 'orders'), orderBy('orderDate', 'desc'));
  } catch {
    // Fallback sur createdAt si orderDate n'existe pas
    q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
  }

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrders(data);
      setLoading(false);
      setError(null);
    },
    (error) => {
      console.error('❌ Real-time orders error:', error);
      setError(error.message);
      setLoading(false);
      toast.error('Failed to load orders');
    }
  );

  // Nettoyer l'écouteur au démontage
  return () => unsubscribe();
}, []);

  useEffect(() => {
    let f=[...orders];
    if (filters.status!=='all') f=f.filter(o=>o.status===filters.status);
    if (filters.priority!=='all') f=f.filter(o=>(o.priority||'normal')===filters.priority);
    if (filters.searchTerm.trim()) { const s=filters.searchTerm.toLowerCase(); f=f.filter(o=>o.orderId?.toLowerCase().includes(s)||o.id?.toLowerCase().includes(s)||o.userName?.toLowerCase().includes(s)||o.userEmail?.toLowerCase().includes(s)||o.items?.some(i=>i.name?.toLowerCase().includes(s))); }
    if (filters.minAmount&&!isNaN(+filters.minAmount)) f=f.filter(o=>getOrderTotal(o)>=+filters.minAmount);
    if (filters.dateRange.startDate) f=f.filter(o=>new Date(o.orderDate||o.createdAt?.toDate?.())>=new Date(filters.dateRange.startDate));
    if (filters.dateRange.endDate)   f=f.filter(o=>new Date(o.orderDate||o.createdAt?.toDate?.())<=new Date(filters.dateRange.endDate+'T23:59:59'));
    setFilteredOrders(f);
  }, [orders, filters, getOrderTotal]);

  // sapi-admin/src/pages/Orders.jsx

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setProcessingAction(true);
      const ref = doc(db, 'orders', orderId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error('Order not found');
      
      const orderData = snap.data();
      const upd = { status: newStatus, timestamp: new Date().toISOString(), note: `${newStatus} by admin` };
      await updateDoc(ref, {
        status: newStatus,
        statusHistory: [...(orderData.statusHistory || []), upd]
      });
      
      // ℹ️ WhatsApp pour Approved/Shipped/Delivered/Cancelled n'est PAS envoyé ici.
      // Le trigger Cloud Function `onOrderStatusUpdated` (index.js) écoute ce même
      // write Firestore et envoie déjà le template correspondant (avec anti-doublon
      // par timestamp). L'envoyer aussi ici causait un double message WhatsApp.

      // ✅ NOTIFICATION POUR LE STATUT 'Packed' (sans WhatsApp, juste notification)
      if (newStatus === 'Packed' && orderData.userId) {
        try {
          await sendAndCleanTokens(orderData.userId, {
            notification: {
              title: "📦 Order Packed",
              body: `Your order #${orderData.orderId || orderId} has been packed and is ready for shipping!`,
            },
            data: { type: "order_status_updated", orderId: orderData.orderId || orderId, status: 'Packed' }
          });
          console.log(`✅ Push notification Packed envoyé à ${orderData.userId}`);
        } catch (e) {
          console.error('❌ Push Packed error:', e.message);
        }
      }
      
      if (isModalOpen && selectedOrder?.id === orderId) {
        setSelectedOrder(p => ({ ...p, status: newStatus }));
        setIsModalOpen(false);
      }
      toast.success(`Order → ${newStatus}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setProcessingAction(false);
    }
  };

    const addTracking = async (e) => {
    e.preventDefault();
    if (!shippingInfo.trackingNumber) {
      toast.error('Enter tracking number');
      return;
    }
    try {
      setProcessingAction(true);
      const ref = doc(db, 'orders', selectedOrder.id);
      const trackingData = { code: shippingInfo.trackingNumber, carrier: shippingInfo.carrier };
      const upd = { status: 'Shipped', timestamp: new Date().toISOString(), note: `Shipped: ${shippingInfo.trackingNumber}` };
      await updateDoc(ref, {
        status: 'Shipped',
        tracking: trackingData,
        statusHistory: [...(selectedOrder.statusHistory || []), upd]
      });
      
      // ✅ Le onSnapshot mettra à jour automatiquement
      
      setIsModalOpen(false);
      setSelectedOrder(null);
      setShippingInfo({ trackingNumber: '', carrier: 'IndiaPost', service: 'standard', weight: '', notes: '' });
      toast.success('Tracking added');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setProcessingAction(false);
    }
  };

  const generateAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      
      // ✅ Utiliser les ordres déjà en mémoire, pas une nouvelle requête
      const totalOrders = filteredOrders.length;
      const totalRevenue = filteredOrders.reduce((s, o) => s + getOrderTotal(o), 0);
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      const totalUniqueCustomers = new Set(filteredOrders.map(o => o.userId).filter(Boolean)).size;

      const statusBreakdown = filteredOrders.reduce((acc, o) => {
        const s = o.status || 'Unknown';
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      }, {});

      setAnalyticsData({
        totalOrders,
        totalRevenue,
        averageOrderValue,
        totalUniqueCustomers,
        statusBreakdown,
        lastUpdated: new Date().toLocaleString()
      });
      setShowAnalytics(true);

    } catch(e) {
      toast.error(e.message);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const totalRevenue    = useMemo(()=>orders.reduce((s,o)=>s+getOrderTotal(o),0),[orders,getOrderTotal]);
  const deliveredCount  = useMemo(()=>orders.filter(o=>o.status==='Delivered').length,[orders]);
  const pendingCount    = useMemo(()=>orders.filter(o=>o.status==='Placed').length,[orders]);
  const clearFilters    = ()=>setFilters({status:'all',priority:'all',searchTerm:'',dateRange:{startDate:'',endDate:''},minAmount:'',carrier:'all'});
  const hasActiveFilters= filters.status!=='all'||filters.priority!=='all'||!!filters.searchTerm||!!filters.minAmount||!!filters.dateRange.startDate;

  const COL = bulkMode
    ? '32px 160px 1fr 90px 110px 80px 80px 160px'
    : '160px 1fr 90px 110px 80px 80px 160px';

  if (loading&&orders.length===0) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:400,gap:16}}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(20,184,166,.3)', borderTopColor: '#14b8a6', animation: 'spin .8s linear infinite' }} />
      <p style={{color:'#64748b',fontSize:13,fontWeight:600}}>Loading orders…</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error&&orders.length===0) return (
  <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:400,gap:12}}>
    <XCircle size={40} color="#f87171"/>
    <p style={{color:'#f87171',fontWeight:700,fontSize:15}}>Failed to load orders</p>
    <p style={{color:'#475569',fontSize:12}}>{error}</p>
    <Btn variant="amber" onClick={() => window.location.reload()}>Retry</Btn>
  </div>
);

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} style={{paddingBottom:48}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input[type=date]::-webkit-calendar-picker-indicator{filter:invert(.5)}`}</style>

      {/* ── Header ── */}
      <div style={{marginBottom:20}}>
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:12,marginBottom:16}}>
          <div>
            <p style={{fontSize:9,textTransform:'uppercase',letterSpacing:'.18em',color:'teal',marginBottom:4}}>Management</p>
            <h1 style={{fontSize:22,fontWeight:800,color:'#fff',display:'flex',alignItems:'center',gap:10}}>
              <ShoppingBag size={20} color="teal"/> Orders
            </h1>
            <p style={{fontSize:11,color:'#475569',marginTop:3}}>Track and manage all customer orders in real time</p>
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {/* ✅ Refresh - juste un indicateur live */}
          <Btn variant="ghost" disabled={loading}>
            <RefreshCw size={13} style={{animation:loading?'spin .8s linear infinite':'none'}}/>
            {loading?'Loading…':'Live ✅'}
          </Btn>
          <Btn variant="amber" onClick={generateAnalytics} disabled={analyticsLoading}>
            <BarChart2 size={13}/> {analyticsLoading?'Loading…':'Analytics'}
          </Btn>
          <Btn variant={bulkMode?'red':'ghost'} onClick={()=>{setBulkMode(v=>!v);setSelectedOrderIds(new Set());}}>
            <CheckSquare size={13}/> {bulkMode?'Exit Bulk':'Bulk Ops'}
          </Btn>
        </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12 }}>
          <MiniStat label="Total Orders"  value={orders.length}             accent="#f59e0b" icon={ShoppingBag} index={0}/>
          <MiniStat label="Showing"       value={filteredOrders.length}     accent="#38bdf8" icon={Search} index={1}/>
          <MiniStat label="Delivered"     value={deliveredCount}            accent="#34d399" icon={CheckCircle2} index={2}/>
          <MiniStat label="Pending"       value={pendingCount}              accent="#fbbf24" icon={Clock} index={3}/>
          <MiniStat label="Revenue (π)"   value={FormatPrice(totalRevenue)} accent="#a78bfa" icon={TrendingUp} index={4}/>
          {selectedOrderIds.size>0 && <MiniStat label="Selected" value={selectedOrderIds.size} accent="#fb923c" icon={BoxSelect} index={5}/>}
        </div>
      </div>

      {/* ── Filters ── */}
      <GlassPanel style={{marginBottom:16}}>
        <button onClick={()=>setFiltersOpen(v=>!v)} style={{display:'flex',alignItems:'center',justifyContent:'space-between',width:'100%',padding:'12px 16px',background:'none',border:'none',cursor:'pointer',color:'#e2e8f0'}}>
          <span style={{display:'flex',alignItems:'center',gap:8,fontSize:12,fontWeight:600}}>
            <Filter size={14} color="#f59e0b"/> Filters &amp; Search
            {hasActiveFilters&&<span style={{background:'rgba(245,158,11,.2)',color:'#f59e0b',fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:20,border:'1px solid rgba(245,158,11,.3)'}}>ACTIVE</span>}
          </span>
          <ChevronDown size={14} style={{transform:filtersOpen?'rotate(180deg)':'none',transition:'transform .2s'}}/>
        </button>
        <AnimatePresence>
          {filtersOpen&&(
            <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} transition={{duration:.2}} style={{overflow:'hidden'}}>
              <div style={{padding:'0 16px 16px',borderTop:'1px solid rgba(255,255,255,.05)'}}>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12,marginTop:14}}>
                  <div>
                    <label style={{fontSize:9,textTransform:'uppercase',letterSpacing:'.12em',color:'#475569',display:'block',marginBottom:6}}>Status</label>
                    <DarkSelect value={filters.status} onChange={e=>setFilters(p=>({...p,status:e.target.value}))}>
                      <option value="all">All Statuses</option>
                      {Object.keys(STATUS_MAP).map(s=><option key={s} value={s}>{STATUS_MAP[s].label}</option>)}
                    </DarkSelect>
                  </div>
                  <div>
                    <label style={{fontSize:9,textTransform:'uppercase',letterSpacing:'.12em',color:'#475569',display:'block',marginBottom:6}}>Priority</label>
                    <DarkSelect value={filters.priority} onChange={e=>setFilters(p=>({...p,priority:e.target.value}))}>
                      <option value="all">All Priorities</option>
                      {Object.keys(PRIORITY_MAP).map(p=><option key={p} value={p}>{PRIORITY_MAP[p].label}</option>)}
                    </DarkSelect>
                  </div>
                  <div>
                    <label style={{fontSize:9,textTransform:'uppercase',letterSpacing:'.12em',color:'#475569',display:'block',marginBottom:6}}>Search</label>
                    <div style={{position:'relative'}}>
                      <Search size={12} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'#475569'}}/>
                      <DarkInput value={filters.searchTerm} onChange={e=>setFilters(p=>({...p,searchTerm:e.target.value}))} placeholder="ID, customer…" style={{paddingLeft:28}}/>
                    </div>
                  </div>
                  <div>
                    <label style={{fontSize:9,textTransform:'uppercase',letterSpacing:'.12em',color:'#475569',display:'block',marginBottom:6}}>From</label>
                    <DarkInput type="date" value={filters.dateRange.startDate} onChange={e=>setFilters(p=>({...p,dateRange:{...p.dateRange,startDate:e.target.value}}))}/>
                  </div>
                  <div>
                    <label style={{fontSize:9,textTransform:'uppercase',letterSpacing:'.12em',color:'#475569',display:'block',marginBottom:6}}>To</label>
                    <DarkInput type="date" value={filters.dateRange.endDate} onChange={e=>setFilters(p=>({...p,dateRange:{...p.dateRange,endDate:e.target.value}}))}/>
                  </div>
                  <div>
                    <label style={{fontSize:9,textTransform:'uppercase',letterSpacing:'.12em',color:'#475569',display:'block',marginBottom:6}}>Min Amount (π)</label>
                    <DarkInput type="number" value={filters.minAmount} onChange={e=>setFilters(p=>({...p,minAmount:e.target.value}))} placeholder="0.00"/>
                  </div>
                </div>
                {hasActiveFilters&&(
                  <div style={{marginTop:12,display:'flex',justifyContent:'flex-end'}}>
                    <Btn variant="red" size="xs" onClick={clearFilters}><X size={11}/> Clear filters</Btn>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </GlassPanel>

      {/* ── Bulk bar ── */}
      <AnimatePresence>
        {bulkMode&&selectedOrderIds.size>0&&(
          <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}} style={{background:'rgba(245,158,11,.08)',border:'1px solid rgba(245,158,11,.25)',borderRadius:12,padding:'10px 16px',marginBottom:12,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <span style={{fontSize:11,color:'#f59e0b',fontWeight:700}}>{selectedOrderIds.size} selected</span>
            <Btn variant="green" size="xs">Approve All</Btn>
            <Btn variant="red"   size="xs">Decline All</Btn>
            <Btn variant="ghost" size="xs" onClick={()=>setSelectedOrderIds(new Set())}><X size={10}/> Clear</Btn>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Table ── */}
      <GlassPanel>
        {/* Header row */}
        <div style={{display:'grid',gridTemplateColumns:COL,gap:8,padding:'10px 16px',borderBottom:'1px solid rgba(255,255,255,.05)',fontSize:9,textTransform:'uppercase',letterSpacing:'.12em',color:'#334155',fontWeight:700,alignItems:'center'}}>
          {bulkMode&&<div><input type="checkbox" checked={selectedOrderIds.size===filteredOrders.length&&filteredOrders.length>0} onChange={()=>{if(selectedOrderIds.size===filteredOrders.length)setSelectedOrderIds(new Set());else setSelectedOrderIds(new Set(filteredOrders.map(o=>o.id)));}} style={{width:14,height:14,accentColor:'#f59e0b'}}/></div>}
          <div>Order</div><div>Customer</div><div>Total</div><div>Status</div><div>Priority</div><div>Date</div><div>Actions</div>
        </div>



        {filteredOrders.length===0 ? (
          <div style={{padding:'48px 24px',textAlign:'center',color:'#334155'}}>
            <Package size={32} style={{margin:'0 auto 12px',opacity:.3}}/>
            <p style={{fontSize:13,fontWeight:600}}>No orders found</p>
            {hasActiveFilters&&<p style={{fontSize:11,marginTop:6,color:'#1e293b'}}>Try clearing the filters</p>}
          </div>
        ) : filteredOrders.map((order, idx) => {
          if (!order?.id) return null;
          const isSelected = selectedOrderIds.has(order.id);
          return (
            <motion.div key={order.id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:Math.min(idx*.02,.3)}}
              style={{display:'grid',gridTemplateColumns:COL,gap:8,padding:'12px 16px',borderBottom:'1px solid rgba(255,255,255,.03)',background:isSelected?'rgba(245,158,11,.05)':'transparent',transition:'background .15s',alignItems:'center'}}
              onMouseEnter={e=>{if(!isSelected)e.currentTarget.style.background='rgba(255,255,255,.02)'}}
              onMouseLeave={e=>{e.currentTarget.style.background=isSelected?'rgba(245,158,11,.05)':'transparent'}}
            >
              {bulkMode&&(
                <div>
                  <input type="checkbox" checked={isSelected} onChange={()=>setSelectedOrderIds(prev=>{const n=new Set(prev);isSelected?n.delete(order.id):n.add(order.id);return n;})} style={{width:14,height:14,accentColor:'#f59e0b'}}/>
                </div>
              )}
              <div>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <span style={{fontSize:12,fontWeight:700,color:'#e2e8f0',fontFamily:'monospace'}}>{getDisplayId(order)}</span>
                  <button onClick={()=>copyToClipboard(getFullId(order))} style={{background:'none',border:'none',cursor:'pointer',padding:3,borderRadius:4,color:'#334155',display:'flex'}}><Copy size={10}/></button>
                </div>
                <p style={{fontSize:10,color:'#334155',marginTop:2}}>{order.items?.length||0} item{order.items?.length!==1?'s':''}</p>
              </div>
              <div>
                <p style={{fontSize:12,fontWeight:600,color:'#cbd5e1',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{order.userName||'Unknown'}</p>
                <p style={{fontSize:10,color:'#334155',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{order.userEmail}</p>
              </div>
              <div style={{fontSize:12,fontWeight:700,color:'#fff'}}>{FormatPrice(getOrderTotal(order))}</div>
              <div><StatusBadge status={order.status}/></div>
              <div><PriorityDot priority={order.priority}/></div>
              <div style={{fontSize:10,color:'#475569'}}>{formatDate(order.orderDate||order.createdAt)}</div>

              <div style={{display:'flex', gap:4, flexWrap:'wrap'}}>
                {/* Bouton View */}
                <Btn size="xs" variant="ghost" onClick={()=>{setSelectedOrder(order);setModalMode('view');setIsModalOpen(true);}}>
                  <Eye size={10}/>
                </Btn>

                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    
                    const phone = order.userPhone || order.phoneNumber || null;
                    if (!phone) {
                      toast.warning('❌ Aucun numéro de téléphone');
                      return;
                    }

                    setProcessingAction(true);
                    try {
                      let templateName = 'order_placed';
                      let params = [];
                      let isTextMessage = false;
                      let textMessage = '';
                      
                      // ✅ Sélection du template selon le statut
                      if (order.status === 'Approved') {
                        templateName = 'order_approved';
                        params = [order.userName || 'Client', order.orderId || order.id];
                      } else if (order.status === 'Packed') {
                        // ✅ NOUVEAU TEMPLATE
                        templateName = 'order_packed';
                        params = [order.userName || 'Client', order.orderId || order.id];
                      } else if (order.status === 'Shipped') {
                        templateName = 'order_shipped';
                        params = [
                          order.orderId || order.id,
                          order.userName || 'Client',
                          order.tracking?.carrier || 'Our delivery partner',
                          order.tracking?.code || 'N/A',
                          order.tracking?.url || 'sapi.etralis.com'
                        ];
                      } else if (order.status === 'Delivered') {
                        templateName = 'order_delivered';
                        params = [order.userName || 'Client', order.orderId || order.id];
                      } else if (order.status === 'Placed') {
                        templateName = 'order_placed';
                        params = [
                          order.userName || 'Client',
                          order.orderId || order.id,
                          (order.totalAmount || order.total || 0).toString(),
                          (order.items?.length || 0).toString(),
                          order.shipping?.method || 'Standard',
                          order.shipping?.address || 'Adresse à confirmer',
                          (order.shipping?.cost || 0).toString()
                        ];
                      } else if (order.status === 'Cancelled') {
                        templateName = 'order_cancelled';
                        params = [order.userName || 'Client', order.orderId || order.id];
                      }

                      let result;
                      if (isTextMessage) {
                        // ✅ Envoyer un message texte libre (fonctionne si conversation active)
                        result = await sendWhatsAppMessage(phone, textMessage);
                      } else {
                        // ✅ Envoyer un template
                        result = await sendWhatsAppTemplate(phone, templateName, params);
                      }
                      
                      if (result.success) {
                        toast.success(`✅ WhatsApp envoyé à ${phone}`);
                      } else {
                        toast.error(`❌ Échec: ${result.error}`);
                      }
                    } catch (error) {
                      console.error('❌ WhatsApp error:', error);
                      toast.error('Erreur lors de l\'envoi');
                    } finally {
                      setProcessingAction(false);
                    }
                  }}
                  disabled={processingAction}
                  className={`px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1.5 transition ${
                    order.userPhone 
                      ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25 border border-green-500/30' 
                      : 'bg-slate-500/10 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </button>
                
                {/* Autres boutons de statut */}
                {order.status==='Placed'&&<>
                  <Btn size="xs" variant="green" onClick={()=>updateOrderStatus(order.id,'Approved')} disabled={processingAction}><Check size={10}/></Btn>
                  <Btn size="xs" variant="red"   onClick={()=>updateOrderStatus(order.id,'Declined')} disabled={processingAction}><X size={10}/></Btn>
                </>}
                {order.status === 'Approved' && (
                  <Btn size="xs" variant="indigo" onClick={() => updateOrderStatus(order.id, 'Packed')} disabled={processingAction}>
                    <Package size={10}/> Pack
                  </Btn>
                )}
                {order.status==='Packed'&&<Btn size="xs" variant="violet" onClick={()=>{setSelectedOrder(order);setModalMode('shipping');setIsModalOpen(true);setShippingInfo(p=>({...p,trackingNumber:order.tracking?.code||'',carrier:order.tracking?.carrier||'IndiaPost'}));}}><Truck size={10}/> Ship</Btn>}
                {order.status==='Shipped'&&<Btn size="xs" variant="green" onClick={()=>updateOrderStatus(order.id,'Delivered')} disabled={processingAction}><Check size={10}/> Done</Btn>}
              </div>
            </motion.div>
          );
        })}

        {filteredOrders.length>0&&(
          <div style={{padding:'10px 16px',borderTop:'1px solid rgba(255,255,255,.04)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <p style={{fontSize:10,color:'#334155'}}>
              Showing <span style={{color:'#f59e0b',fontWeight:700}}>{filteredOrders.length}</span> of <span style={{color:'#f59e0b',fontWeight:700}}>{orders.length}</span> orders
            </p>
          </div>
        )}
      </GlassPanel>

      {/* ── Order Detail Modal ── */}
      <AnimatePresence>
        {isModalOpen&&selectedOrder&&(
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            style={{position:'fixed',inset:0,background:'rgba(0,0,0,.65)',backdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,zIndex:50}}
            onClick={()=>setIsModalOpen(false)}
          >
            <motion.div initial={{scale:.96,opacity:0,y:12}} animate={{scale:1,opacity:1,y:0}} exit={{scale:.96,opacity:0,y:12}} transition={{duration:.2,ease:[0.22,1,0.36,1]}}
              style={{background:'#0d1526',border:'1px solid rgba(255,255,255,.1)',borderRadius:18,width:'100%',maxWidth:580,maxHeight:'88vh',overflow:'hidden',display:'flex',flexDirection:'column'}}
              onClick={e=>e.stopPropagation()}
            >
              <div style={{padding:'16px 20px',borderBottom:'1px solid rgba(255,255,255,.06)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div>
                  <p style={{fontSize:9,textTransform:'uppercase',letterSpacing:'.14em',color:'#475569',marginBottom:3}}>{modalMode==='shipping'?'Add Shipping':'Order Details'}</p>
                  <h3 style={{fontSize:14,fontWeight:700,color:'#fff'}}>{modalMode==='shipping'?'Shipping & Tracking':getDisplayId(selectedOrder)}</h3>
                </div>
                <button onClick={()=>setIsModalOpen(false)} style={{background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.08)',borderRadius:8,padding:'5px 8px',cursor:'pointer',color:'#94a3b8',display:'flex'}}><X size={14}/></button>
              </div>
              <div style={{padding:20,overflowY:'auto',flex:1}}>
                {modalMode==='shipping' ? (
                  <form onSubmit={addTracking} style={{display:'flex',flexDirection:'column',gap:16}}>
                    <div>
                      <label style={{fontSize:11,color:'#64748b',display:'block',marginBottom:6,fontWeight:600}}>Carrier</label>
                      <DarkSelect value={shippingInfo.carrier} onChange={e=>setShippingInfo(p=>({...p,carrier:e.target.value}))}>
                        {(SHIPPING_CARRIERS?Object.values(SHIPPING_CARRIERS):['IndiaPost','FedEx','DHL','UPS']).map(c=>{const name=c.name||c;return <option key={name} value={name}>{name}</option>;})}
                      </DarkSelect>
                    </div>
                    <div>
                      <label style={{fontSize:11,color:'#64748b',display:'block',marginBottom:6,fontWeight:600}}>Tracking Number <span style={{color:'#f59e0b'}}>*</span></label>
                      <DarkInput value={shippingInfo.trackingNumber} onChange={e=>setShippingInfo(p=>({...p,trackingNumber:e.target.value}))} placeholder="Enter tracking number"/>
                    </div>
                    <div style={{display:'flex',justifyContent:'flex-end',gap:8,paddingTop:8,borderTop:'1px solid rgba(255,255,255,.05)'}}>
                      <Btn variant="ghost" onClick={()=>setIsModalOpen(false)}>Cancel</Btn>
                      <Btn variant="violet" type="submit" style={{padding:'7px 18px'}}><Truck size={12}/>{processingAction?'Processing…':'Add Tracking'}</Btn>
                    </div>
                  </form>
                ) : (
                  <div style={{display:'flex',flexDirection:'column',gap:16}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                      {[
                        {label:'Order ID',   value:<span style={{fontFamily:'monospace',color:'#f59e0b'}}>{getFullId(selectedOrder)}</span>},
                        {label:'Status',     value:<StatusBadge status={selectedOrder.status}/>},
                        {label:'Customer',   value:selectedOrder.userName||'—'},
                        {label:'Total',      value:<span style={{color:'#fff',fontWeight:700}}>{FormatPrice(getOrderTotal(selectedOrder))}</span>},
                        {label:'Date',       value:formatDate(selectedOrder.orderDate||selectedOrder.createdAt)},
                        {label:'Priority',   value:<PriorityDot priority={selectedOrder.priority}/>},
                      ].map(({label,value})=>(
                        <div key={label} style={{background:'rgba(255,255,255,.03)',border:'1px solid rgba(255,255,255,.05)',borderRadius:10,padding:'10px 12px'}}>
                          <p style={{fontSize:9,textTransform:'uppercase',letterSpacing:'.12em',color:'#334155',marginBottom:5}}>{label}</p>
                          <div style={{fontSize:12,color:'#94a3b8',fontWeight:500}}>{value}</div>
                        </div>
                      ))}
                    </div>
                    {selectedOrder.items?.length>0&&(
                      <div>
                        <p style={{fontSize:10,textTransform:'uppercase',letterSpacing:'.12em',color:'#334155',marginBottom:8,fontWeight:700}}>Items ({selectedOrder.items.length})</p>
                        <div style={{display:'flex',flexDirection:'column',gap:6}}>
                          {selectedOrder.items.map((item,i)=>(
                            <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',background:'rgba(255,255,255,.03)',border:'1px solid rgba(255,255,255,.05)',borderRadius:8,padding:'8px 12px'}}>
                              <span style={{fontSize:11,color:'#cbd5e1'}}>{item.name} <span style={{color:'#475569'}}>×{item.quantity}</span></span>
                              <span style={{fontSize:11,fontWeight:700,color:'#f59e0b'}}>{FormatPrice((item.price||0)*(item.quantity||0))}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedOrder.tracking&&(
                      <div style={{background:'rgba(139,92,246,.08)',border:'1px solid rgba(139,92,246,.2)',borderRadius:10,padding:'10px 14px'}}>
                        <p style={{fontSize:9,textTransform:'uppercase',letterSpacing:'.12em',color:'#a78bfa',marginBottom:6,fontWeight:700}}>Tracking</p>
                        <p style={{fontSize:11,color:'#cbd5e1'}}>{selectedOrder.tracking.carrier} — <span style={{fontFamily:'monospace',color:'#a78bfa'}}>{selectedOrder.tracking.code}</span></p>
                      </div>
                    )}
                    <div style={{display:'flex',justifyContent:'flex-end',gap:8,paddingTop:8,borderTop:'1px solid rgba(255,255,255,.05)',flexWrap:'wrap'}}>
                      <Btn variant="ghost" onClick={()=>setIsModalOpen(false)}>Close</Btn>
                      {selectedOrder.status==='Packed'&&<Btn variant="violet" onClick={()=>setModalMode('shipping')}><Truck size={12}/> Add Shipping</Btn>}
                      {selectedOrder.status==='Placed'&&<>
                        <Btn variant="green" onClick={()=>updateOrderStatus(selectedOrder.id,'Approved')} disabled={processingAction}><Check size={12}/> Approve</Btn>
                        <Btn variant="red"   onClick={()=>updateOrderStatus(selectedOrder.id,'Declined')} disabled={processingAction}><X size={12}/> Decline</Btn>
                      </>}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Analytics Modal ── */}
      <AnimatePresence>
        {showAnalytics&&analyticsData&&(
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            style={{position:'fixed',inset:0,background:'rgba(0,0,0,.65)',backdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,zIndex:50}}
            onClick={()=>setShowAnalytics(false)}
          >
            <motion.div initial={{scale:.95,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:.95,opacity:0}}
              style={{background:'#0d1526',border:'1px solid rgba(255,255,255,.1)',borderRadius:18,width:'100%',maxWidth:680,maxHeight:'88vh',overflow:'hidden',display:'flex',flexDirection:'column'}}
              onClick={e=>e.stopPropagation()}
            >
              <div style={{padding:'16px 20px',borderBottom:'1px solid rgba(255,255,255,.06)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <h3 style={{fontSize:14,fontWeight:700,color:'#fff',display:'flex',alignItems:'center',gap:8}}><BarChart2 size={16} color="#f59e0b"/> Order Analytics</h3>
                <button onClick={()=>setShowAnalytics(false)} style={{background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.08)',borderRadius:8,padding:'5px 8px',cursor:'pointer',color:'#94a3b8',display:'flex'}}><X size={14}/></button>
              </div>
              <div style={{padding:20,overflowY:'auto',flex:1}}>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:10,marginBottom:16}}>
                  <MiniStat label="Total Orders"     value={analyticsData.totalOrders}                    accent="#38bdf8"/>
                  <MiniStat label="Revenue"          value={FormatPrice(analyticsData.totalRevenue)}      accent="#f59e0b"/>
                  <MiniStat label="Avg Order"        value={FormatPrice(analyticsData.averageOrderValue)} accent="#a78bfa"/>
                  <MiniStat label="Unique Customers" value={analyticsData.totalUniqueCustomers}           accent="#34d399"/>
                </div>
                {analyticsData.statusBreakdown&&(
                  <div>
                    <p style={{fontSize:10,textTransform:'uppercase',letterSpacing:'.14em',color:'#334155',marginBottom:10,fontWeight:700}}>Status Breakdown</p>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(100px,1fr))',gap:8}}>
                      {Object.entries(analyticsData.statusBreakdown).filter(([,c])=>c>0).map(([status,count])=>{
                        const s=STATUS_MAP[status]||STATUS_MAP.Cancelled;
                        return (
                          <div key={status} style={{background:s.bg,border:`1px solid ${s.ring}`,borderRadius:10,padding:'10px 12px',textAlign:'center'}}>
                            <p style={{fontSize:9,color:s.color,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:4}}>{s.label}</p>
                            <p style={{fontSize:20,fontWeight:800,color:'#fff'}}>{count}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <p style={{fontSize:9,color:'#1e293b',marginTop:16,textAlign:'right'}}>Last updated: {analyticsData.lastUpdated||new Date().toLocaleString()}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default Orders;